/**
 * Estacoes — a aparencia da fazenda em cada estacao.
 *
 * primavera (plantar) -> verao (cuidar) -> outono (colher) -> inverno (guardar).
 * O ciclo completo e um Ano (secao 7).
 *
 * D10: a mudanca de estacao E a recompensa. Nao ha tela de "parabens", nao ha
 * estrela: a fazenda muda de cor e a crianca ve que o tempo passou.
 *
 * COMO A COR E APLICADA:
 * O chao e um tapete de centenas de tiles, nao um plano unico. Mas os tiles sao
 * copias de 3 modelos, e copias no Three.js COMPARTILHAM o material. Entao
 * basta tingir esses poucos materiais para o tapete inteiro mudar de cor — sem
 * percorrer centenas de objetos a cada quadro.
 *
 * O tom e sutil de proposito: os modelos ja tem cor propria vinda da paleta, e
 * um tingimento forte apagaria o desenho.
 */

import * as THREE from 'three';

export const ESTACOES_VALIDAS = ['primavera', 'verao', 'outono', 'inverno'];

const APARENCIA = {
  primavera: { chao: 0xffffff, folha: 0xffffff, nome: 'primavera' },
  verao: { chao: 0xf2edd2, folha: 0xe8f0c8, nome: 'verao' },
  outono: { chao: 0xe0b285, folha: 0xf0a860, nome: 'outono' },
  inverno: { chao: 0xdae6f0, folha: 0xd8e4ee, nome: 'inverno' },
};

const DURACAO_TRANSICAO = 2.0; // segundos

export class Estacoes {
  constructor(fazenda) {
    this.fazenda = fazenda;
    this.atual = null;
    this.transicao = null;
  }

  aparenciaDe(estacao) {
    const aparencia = APARENCIA[estacao];
    if (!aparencia) {
      throw new Error(
        `FAZENDA: a estacao "${estacao}" nao existe. ` +
          `Estacoes validas (secao 7): ${ESTACOES_VALIDAS.join(', ')}.`
      );
    }
    return aparencia;
  }

  /**
   * Materiais unicos de um objeto da cena.
   * Como as copias compartilham material, esta lista e curta mesmo quando o
   * objeto tem centenas de malhas.
   */
  materiaisDe(objeto) {
    const materiais = new Set();
    if (!objeto) return [];

    objeto.traverse((filho) => {
      if (!filho.isMesh || !filho.material) return;
      for (const material of [].concat(filho.material)) {
        if (material.color) materiais.add(material);
      }
    });

    return [...materiais];
  }

  /** Aplica a estacao no cenario, com transicao suave de cor. */
  aplicar(estacao, comTransicao = true) {
    const aparencia = this.aparenciaDe(estacao);
    this.atual = estacao;

    const doChao = this.materiaisDe(this.fazenda.chao);
    const dasArvores = this.fazenda.arvores.flatMap((a) => this.materiaisDe(a));

    if (doChao.length === 0) {
      // P5: falha visivel. Sem isto a estacao "funcionaria" sem mudar nada.
      console.warn(
        'FAZENDA: nao achei nenhum material no chao para tingir com a estacao. ' +
          'A fazenda foi montada? Estacoes.aplicar() precisa rodar depois de Fazenda.construir().'
      );
    }

    const alvos = [
      ...doChao.map((m) => ({ material: m, cor: new THREE.Color(aparencia.chao) })),
      ...dasArvores.map((m) => ({ material: m, cor: new THREE.Color(aparencia.folha) })),
    ];

    if (!comTransicao) {
      for (const { material, cor } of alvos) material.color.copy(cor);
      this.transicao = null;
    } else {
      this.transicao = {
        t: 0,
        passos: alvos.map(({ material, cor }) => ({
          material,
          de: material.color.clone(),
          para: cor,
        })),
      };
    }

    console.info(
      `FAZENDA: estacao aplicada — ${aparencia.nome} ` +
        `(${doChao.length} material(is) de chao, ${dasArvores.length} de arvore).`
    );
  }

  atualizar(dt) {
    if (!this.transicao) return;

    this.transicao.t = Math.min(1, this.transicao.t + dt / DURACAO_TRANSICAO);

    for (const passo of this.transicao.passos) {
      passo.material.color.copy(passo.de).lerp(passo.para, this.transicao.t);
    }

    if (this.transicao.t >= 1) this.transicao = null;
  }
}
